import clsx from 'clsx';
import React, { FC, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import Select, { GetOptionLabel, mergeStyles } from 'react-select';
import { toast } from 'react-toastify';
import { AutoCompletePropsV2 } from '../models/autocomplete';
import { capitalizeFirstLetter, useDebounce } from '../utils/AppFunction';
import { KEY, LABEL_POSITION } from '../utils/Constant';
import { autocompleteObjectStyle, multiValueRemove } from './StyleObjectAutocomplete';
import "./style.scss";
// disabled react-hooks/exhaustive-deps
const AutocompleteV2: FC<AutoCompletePropsV2> = forwardRef<any, AutoCompletePropsV2>(
	(props, ref) => {
		const {
			options,
			onChange,
			searchFunction,
			isSearchDefauilt = false,
			renderChildren = "subs",
			hideLabel,
			label,
			labelPosition = '',
			labelClassName,
			labelMargin,
			labelWidth,
			containerClassName,
		} = props;

		const [optionList, setOptionList] = useState<any[]>([]);
		const [isLoading, setIsLoading] = useState<boolean>(false);
		const [selectedValue, setSelectedValue] = useState<any>(null);
		const [isFocus, setIsFocus] = useState<boolean>(false);
		const [pageIndex, setPageIndex] = useState<number>(1);
		const [isCheckDataSrcoll, setIsCheckDataSrcoll] = useState<boolean>(false);
		const [isFirstMount, setIsFirstMount] = useState<boolean>(true);
		const [keyword, setKeyword] = useState('');
		const [previousSearchObject, setPreviousSearchObject] = useState(props.searchObject);
		const debouncedSearchObject = useDebounce(props.searchObject, 500);

		const inputRef = useRef<any>(null);

		let styles = {
			...props?.styles,
			...(props?.isReadOnly ? multiValueRemove : {}),
			control: (styles: any) => ({ ...styles, backgroundColor: 'white' }),
			option: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => {
				const color_bg_selected = '#3C78D8';
				return {
					...styles,
					color: !isFocused ? '#000000' : '#ffffff',
					margin: '5px',
					width: 'calc(100% - 10px)',
					cursor: isDisabled ? 'not-allowed' : 'default',
					borderRadius: '3px',
					zIndex: isSelected ? 9999 : 'auto',
					position: 'relative',
					':active': {
						...styles[':active'],
						backgroundColor: !isDisabled ? color_bg_selected : undefined,
						color: '#ffffff',
					},
					':hover': {
						...styles[':hover'],
						backgroundColor: !isDisabled ? color_bg_selected : undefined,
						color: '#ffffff',
					},
				};
			},
		};
		const combinedStyles = mergeStyles(autocompleteObjectStyle, styles);
		const keywordDebounce = useDebounce(keyword, 800);

		useEffect(() => {
			const shouldFetchData =
				(props.value && !optionList?.length) || (isFocus && !optionList?.length);

			shouldFetchData && fetchData();

			return () => {
				setIsLoading(false);
			};
			//eslint-disable-next-line react-hooks/exhaustive-deps
		}, [options, searchFunction, isFocus, props.value, props.searchObject]);

		useEffect(() => {
			if (isFirstMount) {
				setIsFirstMount(false);
			} else {
				// Kiểm tra xem debouncedSearchObject có thay đổi so với previousSearchObject không
				const hasChanged =
					JSON.stringify(debouncedSearchObject) !== JSON.stringify(previousSearchObject);

				if (hasChanged) {
					// Nếu có sự thay đổi, gọi fetchData
					fetchData();
					// Cập nhật giá trị trước đó
					setPreviousSearchObject(debouncedSearchObject);
				}
			}
			//eslint-disable-next-line react-hooks/exhaustive-deps
		}, [debouncedSearchObject, previousSearchObject]);

		useEffect(() => {
			isSearchDefauilt && fetchData();
			//eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isSearchDefauilt]);

		useEffect(() => {
			searchOption();
			//eslint-disable-next-line react-hooks/exhaustive-deps
		}, [keywordDebounce, selectedValue]);

		useEffect(() => {
			setOptionList([]);
			setSelectedValue(null);
			//eslint-disable-next-line react-hooks/exhaustive-deps
		}, props?.dependencies || []);

		useEffect(() => {
			getValue();
			//eslint-disable-next-line react-hooks/exhaustive-deps
		}, [props.value, optionList]);

		const convertNameUrl = (value: string, item: any) => {
			const array = value.split('.');
			array.forEach(item1 => {
				item = item?.[item1];
			});
			return item;
		};

		const fetchData = async () => {
			if (options?.length > 0 && !props.searchFunction) {
				setOptionList(options);
			} else if (!isLoading) {
				setIsLoading(true);
				try {
					if (props.searchObject !== undefined && props.searchFunction) {
						let data = await getData(props.searchObject);
						let listData: any[] = [];
						if (renderChildren !== null && renderChildren !== undefined) {
							data?.forEach((item: any) => {
								let listChid = getListItemChild(item)
								listData.push(...listChid);
							});
						} else {
							listData = data;
						}

						setPageIndex(props.searchObject?.pageIndex || 1);
						setOptionList(listData || []);
						setIsCheckDataSrcoll(listData?.length > 0);
						setIsLoading(false);
					}
				} catch (error) {
					setIsLoading(false);
					throw error;
				}
			}
		};

		const getListItemChild = (item: any) => {
			let result = []
			const listData = item?.[renderChildren || ""] || item?.subs || [];
			result.push(item);

			listData.forEach((child: any) => {
				let children = getListItemChild(child);
				result.push(...children)
			});
			return result;
		};

		const getData = async (searchObject: any) => {
			const res = await props.searchFunction?.(searchObject);
			return props?.urlData
				? convertNameUrl(props?.urlData, res)
				: res?.data?.data?.content || res?.data?.data;
		};

		const handleChange = (selectedOption: any) => {
			setSelectedValue(selectedOption);
			onChange?.(selectedOption);
		};

		const combinedClassName = clsx(
			props?.className ? props.className : 'w-100',
			clsx(
				props.className,
				props.errors && props.touched && 'ac-is-invalid is-invalid',
				'autocomplete-custom',
				props.hidden && 'd-none'
			)
		);

		const handleFilterByCode = (code: any) => {
			const newCode = code?.toString();
			return optionList?.filter((i) => i?.code?.toString() === newCode || i?.id?.toString() === newCode);
		};

		const getValue = () => {
			if (props?.value) {
				if (typeof props?.value == 'string' || typeof props?.value == 'number') {
					setSelectedValue(handleFilterByCode(props?.value));
				} else if (typeof props?.value == 'object') {
					setSelectedValue(props?.value);
				}
			} else {
				setSelectedValue(null);
			}
		};

		const handleScrollToBottom = async () => {
			try {
				if (isCheckDataSrcoll) {
					let searchObject = { ...props.searchObject };
					searchObject.pageIndex = pageIndex + 1;
					if (props?.labelSearch) searchObject[props?.labelSearch] = keyword;

					setIsLoading(true);
					let data = await getData(searchObject);
					let listData = data?.map((item: any) => ({
						...item,
						name: item?.name || item?.fullName,
					}));
					setPageIndex(pageIndex + 1);
					setIsCheckDataSrcoll(data?.length > 0);
					setOptionList([...optionList, ...listData]);
					setIsLoading(false);
				}
			} catch (error) {
				setIsLoading(false);
				toast.error('Xảy ra lỗi, vui lòng thử lại!');
				throw error;
			}
		};

		const handleKeyDown = async (event: React.KeyboardEvent<HTMLElement>) => {
			if (KEY.SPACE === event.code && !(event.target as HTMLInputElement).value) {
				event.preventDefault();
			}
		};

		const handleInputChange = (newValue: string) => {
			const value = newValue?.trim();
			setKeyword(value);
			props.handleInput && props.handleInput(value);
		};

		const handeBlur = () => {
			setIsFocus(false);
			setKeyword('');
			props?.onBlur?.();
		};

		const searchOption = async () => {
			try {
				if (props?.isSrcoll && !selectedValue) {
					let searchObject = { ...props.searchObject };
					if (props?.labelSearch) searchObject[props?.labelSearch] = keyword;
					setIsLoading(true);
					let data = await getData(searchObject);
					let listData = data?.map((item: any) => ({
						...item,
						name: item?.name || item?.fullName,
					}));
					setPageIndex(props.searchObject?.pageIndex || 1);
					setOptionList(listData);
					setIsCheckDataSrcoll(data?.length > 0);
					setIsLoading(false);
				}
			} catch (error) {
				setIsLoading(false);
				toast.error('Xảy ra lỗi, vui lòng thử lại!');
				throw error;
			}
		};

		const labelStyle = useMemo(() => {
			const marginSide = {
				[LABEL_POSITION.LEFT]: LABEL_POSITION.RIGHT,
				[LABEL_POSITION.RIGHT]: LABEL_POSITION.LEFT,
			};
			const side = capitalizeFirstLetter(marginSide[labelPosition] || LABEL_POSITION.RIGHT);

			return {
				...(labelMargin && { [`margin${side}`]: `${labelMargin}%` }),
				...(labelWidth && { width: `${labelWidth}%` }),
			} as React.CSSProperties;
		}, [labelMargin, labelWidth, labelPosition])

		return (
			<Form.Group className={clsx(
				"w-100 oct-field-wrapper d-flex",
				{ 'flex-column': [LABEL_POSITION.BOTTOM, LABEL_POSITION.TOP].includes(labelPosition) },
				containerClassName
			)}>
				{(label && !hideLabel) && (
					<Form.Label
						className={clsx(
							'text-lable-input max-content-width min-w-fit-content mb-0',
							{ 'me-2': !labelClassName, },
							labelClassName
						)}
						style={labelStyle}
					>
						{label}
					</Form.Label>
				)}
				<div className="w-100 position-relative">
					<Select
						ref={ref || inputRef}
						autoFocus={props?.autoFocus}
						getOptionLabel={(option: GetOptionLabel<any>) =>
							props.getOptionLabel ? props.getOptionLabel(option) : option.name
						}
						getOptionValue={(option: any) =>
							props.getOptionValue ? props.getOptionValue(option) : (option?.value || option.id)
						}
						backspaceRemovesValue={props?.backspaceRemovesValue}
						options={optionList}
						noOptionsMessage={(inputValue: any) =>
							`Không tìm thấy lựa chọn cho "${inputValue}"`
						}
						className={combinedClassName}
						name={props?.name}
						value={selectedValue}
						id={props?.id}
						key={props?.key}
						onFocus={() => setIsFocus(true)}
						onBlur={handeBlur}
						isDisabled={props?.isDisabled || props.disabled}
						isLoading={isLoading}
						styles={combinedStyles}
						minMenuHeight={props?.minMenuHeight}
						maxMenuHeight={props?.maxMenuHeight}
						placeholder={
							<p className="custom-placeholder spaces m-0">
								{props?.placeholder || 'Chọn...'}
							</p>
						}
						onChange={handleChange}
						isMulti={props?.isMulti}
						closeMenuOnSelect={props?.closeMenuOnSelect}
						menuPlacement={props?.menuPlacement ? props?.menuPlacement : 'auto'}
						onMenuScrollToBottom={props?.isSrcoll ? handleScrollToBottom : undefined}
						onKeyDown={handleKeyDown}
						onInputChange={handleInputChange}
						hideSelectedOptions={props?.isReadOnly}
						menuIsOpen={props?.isReadOnly ? false : undefined}
						isSearchable={
							props?.isReadOnly
								? false
								: props?.isSearchable !== undefined
									? props?.isSearchable
									: true
						}
						isClearable={
							props?.isReadOnly
								? false
								: props?.isClearable !== undefined
									? props?.isClearable
									: true
						}
						classNamePrefix="react-select"
					/>
					<Form.Control.Feedback type="invalid" tooltip className="field-tooltip-error">
						{props.errors || ""}
					</Form.Control.Feedback>
				</div>
			</Form.Group>
		);
	}
);
export default AutocompleteV2;
